import { apiClient } from '@/shared/api/client';
import type { ApiResponse } from '@/shared/types/api.types';

// 사용자 상태 타입
export type UserStatus = 'registered' | 'pending' | 'expired';

// 통합 사용자 정보 (초대 + 가입 유저)
export interface User {
  // 공통 필드
  id: string;                  // USER_ID 또는 LINK_CD
  email: string;
  fullName: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  status: UserStatus;          // registered: 가입완료, pending: 초대대기, expired: 만료
  roleCd: string | null;
  roleName: string | null;
  departmentCode?: string | null;
  deptName?: string | null;

  // L_LOGIN_USERS 필드 (가입 완료 시)
  userId: string | null;
  clientCd: string | null;
  contactCd: string | null;
  globalUserCd: string | null;
  lastLoginDate: string | null;
  defaultLangCd: string | null;
  isFirstSignUpUser: boolean;
  platform: string | null;
  mfaIsEnabled: boolean;
  dateFormat: string | null;
  docDateFormat: string | null;
  signUpStepCd: string | null;

  // L_USER_LINK 필드 (초대 정보)
  linkId: number | null;
  linkCd: string | null;
  linkStatusCd: string | null;       // 초대 상태 코드 (A48000, A48001 등)
  linkStatusName: string | null;     // 초대 상태명 (BASECODE에서 조회)
  isExpired: boolean;                // 만료 여부 (EXPIRE_DATE 기준)
  managerUserId: string | null;
  inviteEmail: string | null;
  isActive: boolean;
  linkDate: string | null;
  expireDate: string | null;
  acceptDate: string | null;

  // 등록 정보
  sysRegDate: string | null;
  sysRegId: string | null;
  sysRegName: string | null;
  sysModDate: string | null;
  sysModId: string | null;
  sysModName: string | null;

  // 등록자 정보
  registrantName: string | null;
  registrantEmail: string | null;

  note: string | null;
}

export interface UserListResponse {
  users: User[];
  isAuthEnabled?: boolean;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface UserListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: UserStatus | 'all';
  includeDeleted?: boolean;
}

export interface SendInviteRequest {
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  role: string;
  departmentCode?: string | null;
  note?: string;
}

export interface SendInviteResponse {
  linkCd: string;
  message: string;
}

export interface UpdateUserRequest {
  userId: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  roleCd?: string;
  note?: string;
}

export interface UpdateUserResponse {
  message: string;
}

export interface DeleteUserRequest {
  userId: string;
}

export interface DeleteUserResponse {
  message: string;
}

export interface UserDetailResponse {
  user: User;
}

/**
 * 통합 사용자 목록 조회 (초대 + 가입 유저)
 */
export const getUserList = async (params?: UserListParams): Promise<ApiResponse<UserListResponse>> => {
  const response = await apiClient.get<UserListResponse>('/users/list', { params });
  return response;
};

/**
 * 사용자 상세 조회
 */
export const getUserDetail = async (userId: string): Promise<ApiResponse<UserDetailResponse>> => {
  const response = await apiClient.get<UserDetailResponse>(`/users/detail/${userId}`);
  return response;
};

/**
 * 사용자 초대 발송
 */
export const sendInvite = async (data: SendInviteRequest): Promise<ApiResponse<SendInviteResponse>> => {
  const response = await apiClient.post<SendInviteResponse>('/signup/send-invite', data);
  return response;
};

/**
 * 사용자 정보 수정
 */
export const updateUser = async (data: UpdateUserRequest): Promise<ApiResponse<UpdateUserResponse>> => {
  const response = await apiClient.put<UpdateUserResponse>('/users/update', data);
  return response;
};

/**
 * 사용자 삭제 (탈퇴)
 */
export const deleteUser = async (userId: string): Promise<ApiResponse<DeleteUserResponse>> => {
  const response = await apiClient.delete<DeleteUserResponse>(`/users/delete/${userId}`);
  return response;
};

/**
 * 초대 재발송
 */
export const resendInvite = async (linkCd: string): Promise<ApiResponse<SendInviteResponse>> => {
  const response = await apiClient.post<SendInviteResponse>('/users/resend-invite', { linkCd });
  return response;
};

/**
 * 초대 취소
 */
export const cancelInvite = async (linkCd: string): Promise<ApiResponse<{ message: string }>> => {
  const response = await apiClient.delete<{ message: string }>(`/users/cancel-invite/${linkCd}`);
  return response;
};

/**
 * 최초 방문 Welcome 모달을 본 것으로 표시 (서버 플래그 WELCOME_SEEN_YN = 'Y')
 */
export const markWelcomeSeen = async (): Promise<ApiResponse<{ welcomeSeen: boolean }>> => {
  const response = await apiClient.post<{ welcomeSeen: boolean }>('/users/welcome-seen');
  return response;
};
